import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function buildDefaultWidths(columnDefs) {
  return columnDefs.reduce((acc, col) => {
    acc[col.key] = col.defaultWidth;
    return acc;
  }, {});
}

function loadStoredWidths(storageKey, columnDefs) {
  if (!storageKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const merged = buildDefaultWidths(columnDefs);
    columnDefs.forEach((col) => {
      const n = Number(parsed[col.key]);
      if (!Number.isFinite(n) || n < (col.minWidth ?? 48)) return;
      const capped = Number.isFinite(col.maxWidth) ? Math.min(n, col.maxWidth) : n;
      merged[col.key] = Math.round(capped);
    });
    return merged;
  } catch (_) {
    return null;
  }
}

function persistWidths(storageKey, widths) {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch (_) {
    /* ignore storage errors */
  }
}

function sumColumnWidths(columnDefs, widths) {
  return columnDefs.reduce((sum, col) => sum + (widths[col.key] ?? col.defaultWidth), 0);
}

function findColElement(startEl, key) {
  const table = startEl?.closest?.('table');
  if (!table || !key) return { table: null, col: null, th: null };
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(key) : String(key).replace(/"/g, '\\"');
  const col = table.querySelector(`col[data-col-key="${escaped}"]`);
  const th = startEl?.closest?.('th') || null;
  return { table, col, th };
}

function createResizeGuide(table, th) {
  const scrollParent =
    table?.closest('.assets-inventory-table-scroll, .scans-table-body-scroll, .risks-page-table-scroll') ||
    table?.parentElement ||
    document.body;

  const guide = document.createElement('div');
  guide.className = 'assets-col-resize-guide';
  guide.setAttribute('aria-hidden', 'true');

  const parentRect = scrollParent.getBoundingClientRect();
  const thRect = th?.getBoundingClientRect?.() || table.getBoundingClientRect();
  const tableRect = table.getBoundingClientRect();

  const prevPosition = window.getComputedStyle(scrollParent).position;
  if (prevPosition === 'static') {
    scrollParent.style.position = 'relative';
    scrollParent.dataset.colResizePosPatched = '1';
  }

  guide.style.top = `${Math.max(0, tableRect.top - parentRect.top + scrollParent.scrollTop)}px`;
  guide.style.height = `${Math.max(thRect.height, Math.min(tableRect.height, scrollParent.clientHeight))}px`;
  guide.style.left = `${thRect.right - parentRect.left + scrollParent.scrollLeft}px`;

  scrollParent.appendChild(guide);
  return { guide, scrollParent, originLeft: thRect.right - parentRect.left + scrollParent.scrollLeft, startWidthAnchor: thRect.width };
}

function destroyResizeGuide(ctx) {
  if (!ctx) return;
  ctx.guide?.remove();
  if (ctx.scrollParent?.dataset?.colResizePosPatched === '1') {
    ctx.scrollParent.style.position = '';
    delete ctx.scrollParent.dataset.colResizePosPatched;
  }
}

/**
 * Column resize (preview mode):
 * - During drag: move a lightweight vertical guide only (rows stay visible, no table reflow).
 * - On mouseup: apply the new width once to React state + DOM, then persist.
 */
export function useTableColumnResize(columnDefs, storageKey = '') {
  const [widths, setWidths] = useState(
    () => loadStoredWidths(storageKey, columnDefs) || buildDefaultWidths(columnDefs)
  );
  const resizeRef = useRef(null);
  const widthsRef = useRef(widths);

  useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  const onResizeStart = useCallback(
    (key, event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const colDef = columnDefs.find((c) => c.key === key);
      if (!colDef || colDef.resizable === false) return;

      const startX = event.clientX;
      const startWidth = widthsRef.current[key] ?? colDef.defaultWidth;
      const minWidth = colDef.minWidth ?? 48;
      const maxWidth = colDef.maxWidth;
      const { table, col, th } = findColElement(event.currentTarget, key);
      if (!table) return;

      const guideCtx = createResizeGuide(table, th);
      const liveWidths = { ...widthsRef.current, [key]: startWidth };

      resizeRef.current = {
        key,
        startX,
        startWidth,
        minWidth,
        maxWidth,
        table,
        col,
        liveWidths,
        guideCtx,
      };

      table.classList.add('is-col-resizing');
      event.currentTarget?.classList?.add('is-active');
      const handleEl = event.currentTarget;

      const onMove = (e) => {
        const state = resizeRef.current;
        if (!state) return;
        const delta = e.clientX - state.startX;
        let next = Math.max(state.minWidth, state.startWidth + delta);
        if (Number.isFinite(state.maxWidth)) {
          next = Math.min(state.maxWidth, next);
        }
        next = Math.round(next);
        if (state.liveWidths[state.key] === next) return;
        state.liveWidths[state.key] = next;

        const guideDelta = next - state.startWidth;
        if (state.guideCtx?.guide) {
          state.guideCtx.guide.style.left = `${state.guideCtx.originLeft + guideDelta}px`;
        }
      };

      const onUp = () => {
        const state = resizeRef.current;
        resizeRef.current = null;
        destroyResizeGuide(state?.guideCtx);
        state?.table?.classList.remove('is-col-resizing');
        handleEl?.classList?.remove('is-active');

        const nextWidths = state?.liveWidths ? { ...state.liveWidths } : widthsRef.current;
        const finalWidth = nextWidths[state?.key];
        if (state?.col && Number.isFinite(finalWidth)) {
          state.col.style.width = `${finalWidth}px`;
          state.col.style.minWidth = `${finalWidth}px`;
          state.col.style.maxWidth = `${finalWidth}px`;
          if (state.col.width !== undefined) {
            state.col.width = String(finalWidth);
          }
        }
        if (state?.th && Number.isFinite(finalWidth)) {
          state.th.style.width = `${finalWidth}px`;
          state.th.style.minWidth = `${finalWidth}px`;
          state.th.style.maxWidth = `${finalWidth}px`;
        }
        if (state?.table) {
          const sum = sumColumnWidths(columnDefs, nextWidths);
          state.table.style.width = '100%';
          state.table.style.minWidth = `${sum}px`;
        }

        widthsRef.current = nextWidths;
        setWidths(nextWidths);
        persistWidths(storageKey, nextWidths);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [columnDefs, storageKey]
  );

  const tableMinWidth = useMemo(
    () => sumColumnWidths(columnDefs, widths),
    [columnDefs, widths]
  );

  return { widths, onResizeStart, tableMinWidth, resizingKey: null };
}
